import React, { ReactNode } from 'react';
import {
  Box, Flex, keyframes, Text, useColorModeValue,
} from '@chakra-ui/react';
import useCountUp from './useCountUp';
import { useAppearance } from '../../theme/AppearanceProvider';
import useGlassPanel from '../../theme/glassPanel';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// A bright diagonal light bar sweeps across the resting number every few seconds
// (item 62), then waits off-screen so it reads as a glint, not a strobe.
const sweep = keyframes`
  0%        { transform: translateX(-160%) skewX(-18deg); }
  18%, 100% { transform: translateX(520%) skewX(-18deg); }
`;

interface StatCardProps {
  label: string;
  value: number;
  format?: (n: number) => string;
  sub?: string;
  icon?: ReactNode;
  /** theme color token for the glow/accent, e.g. 'brandGreen.400' */
  accent?: string;
  /** stagger index for the entrance animation */
  index?: number;
  footer?: ReactNode;
}

/**
 * The Aurora-Glass bento tile: frosted glass floating on the aurora, a count-up
 * value in gradient text, a glowing top accent, springy hover-lift, and a
 * staggered fade-up entrance. The shared building block of the dashboard.
 */
const StatCard: React.FC<StatCardProps> = ({
  label, value, format, sub, icon, accent = 'brandGreen.400', index = 0, footer,
}) => {
  const { aurora, motion } = useAppearance();
  const animated = useCountUp(value);
  // item 94: count-up is FUNCTIONAL motion — run it in both skins (Accountant
  // included), off only under OS reduce-motion. useCountUp self-guards reduce-motion.
  const shown = motion ? animated : value;
  const display = format ? format(shown) : String(Math.round(shown));
  const glass = useGlassPanel(); // item 406 — the ONE glass material
  const labelColor = useColorModeValue('gray.500', 'whiteAlpha.700');
  const accentVar = `var(--chakra-colors-${accent.replace('.', '-')})`;
  const valueGradient = useColorModeValue(
    'linear(to-br, gray.800, gray.600)',
    `linear(to-br, white, ${accent})`,
  );
  // the light-bar streak that sweeps over the number (item 62)
  const streakColor = useColorModeValue('rgba(0,0,0,0.18)', 'rgba(255,255,255,0.55)');

  return (
    <Flex
      {...glass}
      direction="column"
      position="relative"
      overflow="hidden"
      h="full"
      p={6}
      // item 94: the staggered fade-up entrance is functional motion — keep it in
      // both skins (off under reduce-motion), but the glass/glow stays aurora-only.
      opacity={motion ? 0 : 1}
      animation={motion ? `${fadeUp} 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${index * 0.08}s forwards` : undefined}
      transition="transform 0.25s cubic-bezier(0.22,1,0.36,1), box-shadow 0.25s, border-color 0.25s"
      // item 94: hover-lift is functional (both skins under motion); the accent GLOW
      // is decoration (aurora only).
      _hover={motion ? {
        transform: 'translateY(-6px)',
        borderColor: accent,
        ...(aurora ? { boxShadow: `0 16px 48px rgba(0,0,0,0.5), 0 0 32px ${accentVar}55` } : {}),
      } : undefined}
    >
      {/* glowing top accent edge (Aurora only) */}
      {aurora && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          h="3px"
          bgGradient={`linear(to-r, transparent, ${accent}, transparent)`}
          boxShadow={`0 0 14px ${accentVar}`}
        />
      )}
      <Flex align="center" justify="space-between" mb={3}>
        <Text fontSize="xs" fontWeight="bold" letterSpacing="0.14em" textTransform="uppercase" color={aurora ? labelColor : 'ledgerMuted'}>
          {label}
        </Text>
        {icon && <Box color={aurora ? accent : 'ledgerMuted'} fontSize="lg" opacity={0.9}>{icon}</Box>}
      </Flex>
      <Box position="relative" overflow="hidden" w="fit-content">
        <Text
          fontSize={{ base: '3xl', md: '4xl' }}
          fontWeight="extrabold"
          lineHeight="1"
          letterSpacing="-0.02em"
          color={aurora ? undefined : 'ledgerInk'}
          bgGradient={aurora ? valueGradient : undefined}
          bgClip={aurora ? 'text' : undefined}
        >
          {display}
        </Text>
        {/* light bar sweeping over the total (item 62) — Aurora only. item 406: gated
            on the motion tier like the entrance above — an infinite mix-blend-mode
            compositing animation PER CARD is real GPU cost on reduced-effects rigs */}
        {aurora && motion && (
          <Box
            aria-hidden
            position="absolute"
            top="-20%"
            bottom="-20%"
            left={0}
            w="45%"
            pointerEvents="none"
            bgGradient={`linear(to-r, transparent, ${streakColor}, transparent)`}
            sx={{
              mixBlendMode: 'screen',
              animation: `${sweep} 4.5s ease-in-out infinite`,
              '@media (prefers-reduced-motion: reduce)': { animation: 'none', display: 'none' },
            }}
          />
        )}
      </Box>
      {sub && <Text mt={2} fontSize="sm" color={labelColor}>{sub}</Text>}
      {footer && <Box mt="auto" pt={5}>{footer}</Box>}
    </Flex>
  );
};

StatCard.defaultProps = {
  format: undefined,
  sub: undefined,
  icon: undefined,
  accent: 'brandGreen.400',
  index: 0,
  footer: undefined,
};

export default StatCard;
